/**
 * BentoMotion — iter87.59 shared Framer Motion primitives for
 * "slick open/close" modal and tile interactions across the One UI
 * Bento surface. Consolidates the animation vocabulary so every
 * modal (Takeover, Power Up, Matchday Boost, Pot Picks, Knockout,
 * Rivalries Intel, Recap chart, etc.) and every tile (BentoHome,
 * MyTeamsGrid, TemplatePicker) shares the same tactile feel:
 *
 *   • Backdrop fades + ramps a 12→20px blur in
 *   • Panel spring-scales from 0.92 with a soft translateY lift
 *   • Exit mirrors the entrance so closing feels intentional
 *   • Tiles get staggered entrance, hover lift, and tap-press
 *
 * All primitives respect `prefers-reduced-motion` (Framer Motion
 * honours this globally through <MotionConfig> — see App.jsx).
 *
 * Every primitive preserves the `data-testid` contract used by the
 * pytest UAT suite so no assertions break.
 */
import { forwardRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

/* --------------------------------------------------------------
 * Shared easing / timing so every surface animates in lock-step.
 * ------------------------------------------------------------ */
export const BENTO_SPRING = {
    type: "spring",
    stiffness: 380,
    damping: 32,
    mass: 0.85,
};

export const BENTO_EASE = [0.22, 1, 0.36, 1]; // "expo-out"-ish

/* --------------------------------------------------------------
 * Variants
 * ------------------------------------------------------------ */
const backdropVariants = {
    hidden: {
        opacity: 0,
        backdropFilter: "blur(0px)",
        WebkitBackdropFilter: "blur(0px)",
    },
    visible: {
        opacity: 1,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        transition: { duration: 0.22, ease: BENTO_EASE },
    },
    exit: {
        opacity: 0,
        backdropFilter: "blur(0px)",
        WebkitBackdropFilter: "blur(0px)",
        transition: { duration: 0.16, ease: BENTO_EASE },
    },
};

const panelVariants = {
    hidden: { opacity: 0, y: 18, scale: 0.94 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { ...BENTO_SPRING, delay: 0.04 },
    },
    exit: {
        opacity: 0,
        y: 12,
        scale: 0.96,
        transition: { duration: 0.16, ease: BENTO_EASE },
    },
};

const tileContainerVariants = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.045, delayChildren: 0.04 },
    },
};

const tileVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.97 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { ...BENTO_SPRING },
    },
};

/* --------------------------------------------------------------
 * BentoBackdrop — full-viewport tinted glass overlay with blur ramp
 * ------------------------------------------------------------ */
export const BentoBackdrop = forwardRef(function BentoBackdrop(
    { onClick, className = "", children, testId, ...rest },
    ref,
) {
    return (
        <motion.div
            ref={ref}
            data-testid={testId}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClick}
            className={
                "fixed inset-0 z-50 bg-black/70 flex flex-col items-stretch " +
                "justify-stretch p-2 pt-[max(0.5rem,env(safe-area-inset-top))] " +
                "pb-[max(0.5rem,env(safe-area-inset-bottom))] " +
                className
            }
            {...rest}
        >
            {children}
        </motion.div>
    );
});

/* --------------------------------------------------------------
 * BentoPanel — spring-scaled modal content shell. Consumers style
 * the outer chrome (border tone, padding, max-width) and drop it
 * inside a BentoBackdrop.
 * ------------------------------------------------------------ */
export const BentoPanel = forwardRef(function BentoPanel(
    { className = "", children, onClick, testId, ...rest },
    ref,
) {
    return (
        <motion.div
            ref={ref}
            data-testid={testId}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => {
                e.stopPropagation();
                onClick?.(e);
            }}
            className={className}
            style={{ transformOrigin: "50% 40%" }}
            {...rest}
        >
            {children}
        </motion.div>
    );
});

/* --------------------------------------------------------------
 * BentoModal — composed backdrop + panel with AnimatePresence.
 * Handles body-scroll lock + Escape key. Renders through a portal
 * so it always sits above sibling stacking contexts.
 *
 * Props:
 *   open           — controls presence (drives AnimatePresence)
 *   onClose        — called on backdrop click / Escape
 *   panelClassName — className applied to the animated panel
 *   backdropClassName — extra classes for the backdrop
 *   testId         — data-testid on the backdrop (the "root")
 *   panelTestId    — data-testid on the panel (fallback = `${testId}-panel`)
 * ------------------------------------------------------------ */
export function BentoModal({
    open,
    onClose,
    children,
    panelClassName = "",
    backdropClassName = "",
    testId,
    panelTestId,
    closeOnBackdrop = true,
    lockScroll = true,
}) {
    useEffect(() => {
        if (!open) return;
        // iter87.199.178 — Don't steal Escape from a Radix popper (Popover/
        // Select/DropdownMenu) floating above the modal — e.g. TemplatePicker's
        // scoring-info popover. Radix's own DismissableLayer already closes
        // the topmost layer on Escape; let that happen first instead of also
        // closing (and unmounting) this whole modal underneath it.
        // iter87.534 (testing-agent-reported) — Poppers aren't the only
        // thing that can float above this modal; a Radix Dialog (e.g. the
        // Trophy Tokens walkthrough video opened from inside a Takeover)
        // does too, but it doesn't render a popper wrapper. Worse, Radix's
        // own Escape handling runs inside React's event system attached to
        // the app root, which sits BELOW `document` in the bubble chain —
        // so by the time a normal bubble-phase listener on `document` ran,
        // the Dialog had already closed itself and the guard below saw a
        // stale "closed" state. Listening in the CAPTURE phase instead
        // (top-down, document first) runs this check before Radix's own
        // handling ever fires, so the DOM still reflects the dialog as
        // open when we check it.
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
            if (document.querySelector("[role='dialog'][data-state='open']")) return;
            onClose?.();
        };
        document.addEventListener("keydown", onKey, true);
        let prev = "";
        if (lockScroll) {
            prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.removeEventListener("keydown", onKey, true);
            if (lockScroll) document.body.style.overflow = prev;
        };
    }, [open, onClose, lockScroll]);

    const tree = (
        <AnimatePresence>
            {open && (
                <BentoBackdrop
                    testId={testId}
                    className={backdropClassName}
                    onClick={(e) => {
                        if (!closeOnBackdrop) return;
                        if (e.target === e.currentTarget) onClose?.();
                    }}
                >
                    <BentoPanel
                        testId={panelTestId || (testId ? `${testId}-panel` : undefined)}
                        className={panelClassName}
                    >
                        {children}
                    </BentoPanel>
                </BentoBackdrop>
            )}
        </AnimatePresence>
    );

    if (typeof document === "undefined") return null;
    return createPortal(tree, document.body);
}

/* --------------------------------------------------------------
 * BentoTileGrid — parent that staggers children on mount.
 * Use ANY layout classes as `className`; the grid keeps its
 * original CSS grid shape.
 * ------------------------------------------------------------ */
export const BentoTileGrid = forwardRef(function BentoTileGrid(
    { className = "", children, testId, ...rest },
    ref,
) {
    return (
        <motion.div
            ref={ref}
            data-testid={testId}
            variants={tileContainerVariants}
            initial="hidden"
            animate="visible"
            className={className}
            {...rest}
        >
            {children}
        </motion.div>
    );
});

/* --------------------------------------------------------------
 * BentoTile — animated tile with staggered entrance, hover lift,
 * and tap press. `as` picks the underlying element ("button" for
 * onClick handlers, "a" for links, "div" for static tiles).
 * ------------------------------------------------------------ */
export const BentoTile = forwardRef(function BentoTile(
    { as = "button", selected = false, disabled = false, className = "", children, testId, ...rest },
    ref,
) {
    const Comp = motion[as] || motion.button;
    return (
        <Comp
            ref={ref}
            data-testid={testId}
            variants={tileVariants}
            whileHover={disabled ? undefined : { y: -2, scale: 1.01 }}
            whileTap={disabled ? undefined : { scale: 0.975 }}
            transition={BENTO_SPRING}
            data-selected={selected ? "true" : undefined}
            aria-pressed={as === "button" ? selected || undefined : undefined}
            disabled={as === "button" ? disabled : undefined}
            className={className}
            {...rest}
        >
            {children}
        </Comp>
    );
});

export default BentoModal;
